/**
 * Artifact Skills Card
 * 
 * Displays skill parameters assigned to an artifact in the chat interface
 */

import React, { useState, useEffect } from 'react';
import { List, Tag, Space, Empty, Spin, Badge } from 'antd';
import { CardSectionProps } from '../../../types/compositeCards';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import styles from './ArtifactSkillsCard.module.css';

export const ArtifactSkillsCard: React.FC<CardSectionProps> = ({ 
  card 
}) => {
  const { data } = card;
  
  // Initialize hooks first (before any conditional returns)
  const artifactId = 'artifactId' in data ? data.artifactId : null;
  const initialSkills = 'skills' in data ? data.skills : undefined;
  const [skills, setSkills] = useState(initialSkills || []);
  const [loading, setLoading] = useState(!initialSkills);

  useEffect(() => {
    if (!initialSkills && artifactId) {
      void loadSkills();
    }
  }, [artifactId]);
  
  // Type guard to ensure we have artifact skills data
  if (!('artifactId' in data)) {
    return null;
  }
  
  const { artifactName } = data;

  const loadSkills = async () => {
    if (!artifactId) return;
    
    setLoading(true);
    try {
      const params = await PrimeArtifactService.getArtifactParameters(artifactId);
      setSkills(params);
    } catch (error) {
      console.error('Failed to load skills:', error);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  const renderHierarchyPath = (hierarchyPath: Array<{ name: string; skill_level: number }>) => {
    if (!hierarchyPath || hierarchyPath.length === 0) return null;
    
    return (
      <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
        {hierarchyPath.map((item, index) => (
          <span key={index}>
            {index > 0 && ' > '}
            <span style={{ marginRight: 2 }}>L{item.skill_level}:</span>
            {item.name}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.artifactSkillsCard}>
      <div className={styles.skillsHeader}>
        <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>
          Skill Parameters
        </h4>
        <Badge 
          count={skills.length} 
          style={{ backgroundColor: '#1890ff' }}
          showZero
        />
      </div>

      <Spin spinning={loading}>
        {skills.length > 0 ? (
          <List
            size="small"
            dataSource={skills}
            renderItem={(param: any) => (
              <List.Item className={styles.skillListItem}>
                <List.Item.Meta
                  title={
                    <Space size="small">
                      <strong style={{ fontSize: '12px' }}>
                        {param.skill?.name || param.name}
                      </strong>
                      {param.skill?.skill_level !== undefined && (
                        <Tag color="blue" style={{ fontSize: '10px', padding: '0 4px' }}>
                          Level {param.skill.skill_level}
                        </Tag>
                      )}
                      {param.is_required && (
                        <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>
                          Required
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <div style={{ fontSize: '11px' }}>
                      {param.skill?.description && (
                        <div style={{ marginBottom: 4, color: '#666' }}>
                          {param.skill.description}
                        </div>
                      )}
                      {param.skill?.hierarchy_path && renderHierarchyPath(param.skill.hierarchy_path)}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="No skills assigned to this artifact"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '16px 0', fontSize: '11px' }}
          />
        )}
      </Spin>

      {skills.length > 0 && (
        <div className={styles.skillsSummary}>
          <Space size="small" style={{ fontSize: '11px', color: '#666' }}>
            <span>Total Skills:</span>
            <strong>{skills.length}</strong>
          </Space>
        </div>
      )}
    </div>
  );
};

